import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  SimpleGrid,
  Flex,
  Heading,
  Container,
  VStack,
  Skeleton,
  Text,
  Stack,
  HStack,
  Icon,
  useToast,
  useColorMode,
} from "@chakra-ui/react";
import {
  FaSun,
  FaCloudSun,
  FaCloud,
  FaSmog,
  FaCloudShowersHeavy,
  FaCloudRain,
  FaSnowflake,
  FaBolt,
  FaCheckCircle,
  FaBell,
  FaTemperatureHigh,
} from "react-icons/fa";

import useAxios from "../../utils/useAxios";
import Navbar from "../../components/Navbar";
import FieldTile from "../../components/FieldComponents.tsx/FieldTile";
import { Area, IFieldId } from "../../types/fieldInterface";
import authContext from "../../context/AuthContext";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { IWeatherForecast } from "../../types/weatherInterface";
import { BulkStatusResponse, AlarmOccurrence } from "../../types/alarmInterface";
import { weatherCodeToIcon, weatherDescription } from "../../utils/weatherIconUtils";
import { useTileColors } from "../../utils/colorUtils/tileColorUtil";
import { useMoistureColor } from "../../utils/colorUtils/soilMoistitureColorUtil";
import { getFieldStatusLabel } from "../../utils/fieldStatusUtils";

const FieldsNew = () => {
  const api = useAxios();
  const navigate = useNavigate();
  const toast = useToast();

  const [fields, setFields] = useState<IFieldId[]>([]);
  const [loading, setLoading] = useState(true);

  const { user } = useContext(authContext);
  const { noAlarmBgColor, yellowBgColor, redBgColor } = useTileColors();
  const { pwpColor, damageColor, deficitColor, goodColor, highColor, runoffColor, undefinedColor } =
    useMoistureColor();
  const [weather, setWeather] = useState<IWeatherForecast | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [yellowCount, setYellowCount] = useState(0);
  const [redCount, setRedCount] = useState(0);
  const [imagesByField, setImagesByField] = useState<Record<number, string>>({});
  const [fieldStatuses, setFieldStatuses] = useState<Record<number, string>>({});
  const [noAlarmCount, setNoAlarmCount] = useState(0);
  const [alarmsLoading, setAlarmsLoading] = useState(true);
  const [activeAlarmsByField, setActiveAlarmsByField] = useState<Record<number, number>>({});
  const [redAlarmsByField, setRedAlarmsByField] = useState<Record<number, number>>({});
  const [statusesGridLoading, setStatusGridLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      const cached = localStorage.getItem("fields");
      try {
        const fRes = await api.get<IFieldId[]>("/agtech/fields/");
        if (cancelled) return;
        setFields(fRes.data);
        localStorage.setItem("fields", JSON.stringify(fRes.data));
      } catch (err: any) {
        if (cancelled) return;
        if (cached) {
          setFields(JSON.parse(cached));
        }
        if (err.response?.status !== 401) {
          toast({
            title: "Error fetching fields",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          try {
            const res = await api.get<IWeatherForecast>(
              `/agtech/weather-forecast/current/?lat=${coords.latitude}&lon=${coords.longitude}`,
            );
            if (cancelled) return;
            setWeather(res.data);
          } catch (err: any) {
            if (cancelled) return;
            if (err.response?.status === 401) {
              return;
            }
            toast({
              title: "Error fetching weather data",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          } finally {
            if (cancelled) return;
            setWeatherLoading(false);
          }
        },
        (err: any) => {
          if (cancelled) return;
          if (err.response?.status === 401) {
            return;
          }
          console.warn("geolocation error", err);
          toast({
            title: "Unable to get your location for weather data",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          setWeatherLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 },
      );
    } else {
      setWeatherLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!fields.length) {
      setAlarmsLoading(false);
      setStatusGridLoading(false);
      setYellowCount(0);
      setRedCount(0);
      setNoAlarmCount(0);
      return;
    }

    let cancelled = false;
    setAlarmsLoading(true);

    const ids = fields.map((f) => f.id);

    (async () => {
      try {
        const alRes = await api.get<AlarmOccurrence[]>("/frontend/alarms/", {
          params: { state: "active" },
        });
        if (cancelled) return;

        const activeMap: Record<number, number> = {};
        const redMap: Record<number, number> = {};
        let yellowSum = 0;
        let redSum = 0;
        let noAlarm = 0;

        ids.forEach((fieldId) => {
          activeMap[fieldId] = 0;
          redMap[fieldId] = 0;
        });

        alRes.data.forEach((alarm) => {
          if (!alarm.field || !activeMap.hasOwnProperty(alarm.field)) return;

          activeMap[alarm.field] += 1;
          if (alarm.severity === "ERROR") {
            redMap[alarm.field] += 1;
            redSum += 1;
          } else {
            yellowSum += 1;
          }
        });

        noAlarm = ids.filter((fieldId) => activeMap[fieldId] === 0).length;

        setActiveAlarmsByField(activeMap);
        setRedAlarmsByField(redMap);
        setYellowCount(yellowSum);
        setRedCount(redSum);
        setNoAlarmCount(noAlarm);
        setStatusGridLoading(false);
      } catch (e) {
        toast({ title: "error while fetching alarms", status: "error", isClosable: true });
      } finally {
        if (!cancelled) setStatusGridLoading(false);
        if (!cancelled) setAlarmsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fields]);

  useEffect(() => {
    if (!fields.length) {
      setFieldStatuses({});
      return;
    }

    let cancelled = false;

    const ids = fields.map((f) => f.id);
    (async () => {
      try {
        const stRes = await api.post<BulkStatusResponse>("/frontend/devices/status/bulk", {
          field_ids: ids,
        });
        if (cancelled) return;

        const statuses: Record<number, string> = {};
        Object.entries(stRes.data).forEach(([id, v]) => {
          statuses[Number(id)] = getFieldStatusLabel(v);
        });
        setFieldStatuses(statuses);
      } catch (e) {
        if (cancelled) return;
        const fallbackStatuses: Record<number, string> = {};
        ids.forEach((id) => {
          fallbackStatuses[id] = "Undefined";
        });
        setFieldStatuses(fallbackStatuses);
        toast({
          title: "error fwhile fetching devices statuses",
          status: "error",
          isClosable: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fields]);

  const handleImageChange = (fieldId: number, imageUrl: string) => {
    setImagesByField((prev) => ({ ...prev, [fieldId]: imageUrl }));
  };

  const todayTemp = weather?.actual_temp ?? null;
  const todayCode = weather?.daily.weather_code?.[0];
  const TodayIcon = todayCode != null ? weatherCodeToIcon[todayCode] || FaCloud : FaCloud;

  const soilColorMap: Record<string, string> = {
    PWP: pwpColor,
    Damage: damageColor,
    Deficit: deficitColor,
    Good: goodColor,
    High: highColor,
    Runoff: runoffColor,
    Undefined: undefinedColor,
    [getFieldStatusLabel({ field_status: "Undefined", field_capacity_learning: true })]:
      undefinedColor,
  };

  function paintSoilStat(color: string): string {
    return soilColorMap[color] ?? color;
  }

  // Compute time-based greeting
  const hour = new Date().getHours();
  const greeting =
    hour >= 18 ? (
      <Trans>Good evening</Trans>
    ) : hour >= 12 ? (
      <Trans>Good afternoon</Trans>
    ) : (
      <Trans>Good morning</Trans>
    );

  return (
    <Navbar>
      <Skeleton isLoaded={!loading}>
        <Container maxW="7xl" py={6}>
          <Box>
            <Heading size="xl" mb={1}>
              {greeting}, {user?.first_name}
            </Heading>
            <Text color="gray.500" fontSize={18}>
              <Trans> Here’s how your fields are looking right now </Trans>
            </Text>
          </Box>

          <Flex wrap="wrap" mb={10} mt={10} align="flex-start" justify="space-between">
            <Stack flexBasis={{ base: "100%", md: "60%" }} spacing={4} mr={{ base: 0, md: 6 }}>
              <Heading size="lg">
                <Trans>Status Overview</Trans>
              </Heading>
              <Skeleton isLoaded={!statusesGridLoading} w="auto">
                <SimpleGrid
                  columns={{ base: 3 }}
                  spacingX={4}
                  spacingY={2}
                  w={{ base: "100%", md: "75%" }}
                  maxW={{ base: "100%", md: "500px" }}
                  mx="auto"
                  mt={4}
                >
                  {[
                    {
                      icon: FaCheckCircle,
                      count: noAlarmCount,
                      type: "fields",
                      bg: noAlarmBgColor,
                    },
                    { icon: FaBell, count: yellowCount, type: "alerts", bg: yellowBgColor },
                    { icon: FaBell, count: redCount, type: "fields", bg: redBgColor },
                  ].map(({ icon, count, type, bg }, i) => (
                    <Flex key={i} bg={bg} p={2} borderRadius="md" align="center" justify="center">
                      <Icon as={icon} boxSize={4} flexShrink={0} />
                      <Text fontWeight="semibold" ml={2} noOfLines={1} fontSize="sm">
                        {count}{" "}
                        {type === "fields" ? (
                          count === 1 ? (
                            <Trans>Field</Trans>
                          ) : (
                            <Trans>Fields</Trans>
                          )
                        ) : count === 1 ? (
                          <Trans>Alert</Trans>
                        ) : (
                          <Trans>Alerts</Trans>
                        )}
                      </Text>
                    </Flex>
                  ))}
                </SimpleGrid>
              </Skeleton>
            </Stack>

            <Stack flexBasis={{ base: "100%", md: "35%" }} spacing={4} mt={{ base: 8, md: 1 }}>
              <Heading size="lg">
                <Trans> Today's Weather</Trans>
              </Heading>
              <Box>
                {weatherLoading ? (
                  <Skeleton height="40px" />
                ) : todayTemp ? (
                  <Flex direction={"row"} align={"center"} gap={{ base: 2, md: 4 }}>
                    <HStack spacing={2}>
                      <Icon as={FaTemperatureHigh} />
                      <Text fontSize="2xl" fontWeight="bold">
                        {todayTemp}°
                      </Text>
                    </HStack>
                    <HStack color="gray.600" spacing={2} flexWrap="wrap" ml={{ base: 2, sm: 4 }}>
                      <Icon as={TodayIcon} boxSize={5} />
                      <Text noOfLines={1}>{weatherDescription(todayCode)}</Text>
                    </HStack>
                  </Flex>
                ) : (
                  <Text color="gray.500" fontSize="lg">
                    <Trans>No weather</Trans>
                  </Text>
                )}
              </Box>
            </Stack>
          </Flex>

          <Stack align="left" mb={4}>
            <Heading size="lg" mb={2}>
              <Trans>My Fields</Trans>
            </Heading>
            <Button
              colorScheme="black"
              variant="outline"
              onClick={() => navigate("/fields/add")}
              w="110px"
              size={{ base: "sm", lg: "md" }}
              borderRadius={"unset"}
              p={2}
              flexShrink={0}
              fontStyle="semibold"
              _hover={{ bg: "blackAlpha.100" }}
            >
              <Trans>Add Field</Trans>
            </Button>
          </Stack>

          <VStack spacing={6} align="stretch" mt={4}>
            {fields.length === 0 ? (
              <Text align={"center"} colorScheme="gray">
                No fields yet
              </Text>
            ) : (
              fields.map((field) => {
                const soilLoading = fieldStatuses[field.id] == null;

                const alertCount = alarmsLoading ? 0 : (activeAlarmsByField[field.id] ?? 0);
                const fieldStatusValue = fieldStatuses[field.id];
                console.log("status: ", field.field_name, fieldStatuses[field.id]);
                console.log("alertCount", alertCount);

                const redAlertCount = alarmsLoading ? 0 : (redAlarmsByField[field.id] ?? 0);

                const alertColor =
                  redAlertCount > 0 ? redBgColor : alertCount > 0 ? yellowBgColor : noAlarmBgColor;

                return (
                  <FieldTile
                    key={field.id}
                    id={field.id}
                    name={field.field_name}
                    alertCount={alertCount}
                    soilStatus={fieldStatusValue}
                    soilColor={paintSoilStat(fieldStatusValue)}
                    alertColor={alertColor}
                    imageUrl={imagesByField[field.id] ?? null}
                    onImageChange={handleImageChange}
                    alertsLoading={alarmsLoading}
                    soilLoading={soilLoading}
                  />
                );
              })
            )}
          </VStack>
        </Container>
      </Skeleton>
    </Navbar>
  );
};

export default FieldsNew;
